"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();
  const post = posts[0];

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="space-y-6">
          <div className="h-12 w-3/4 rounded-lg bg-muted/30 animate-pulse" />
          <div className="h-96 w-full rounded-xl bg-muted/30 animate-pulse" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-4 rounded bg-muted/30 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground">Failed to load post content.</p>
        <Link to="/BlogPage" className="mt-4 inline-block">
          <Button variant="outline">
            <ArrowLeft />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="bg-background">
      {}
      <div className="border-b">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <Link to="/BlogPage">
            <Button variant="ghost" size="sm">
              <ArrowLeft />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>

      {}
      <header className="container mx-auto max-w-4xl px-4 py-8">
        <div className="space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
            {post.title}
          </h1>

          {}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {post.author && (
              <div className="flex items-center gap-2">
                <Avatar className="size-8">
                  {post.author.avatar && (
                    <AvatarImage 
                      src={post.author.avatar["48"]} 
                      alt={post.author.name}
                    />
                  )}
                  <AvatarFallback>
                    <User className="size-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-foreground">{post.author.name}</span>
              </div>
            )}

            {post.date && (
              <div className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </time>
              </div>
            )}
          </div>

          {}
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.categories.map((category) => (
                <span
                  key={category.id}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-light text-primary px-3 py-1 text-xs font-medium"
                >
                  {category.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="container mx-auto max-w-4xl px-4 pb-8">
          <div className="relative overflow-hidden rounded-xl shadow-soft">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      )}

      {}
      <div className="container mx-auto max-w-4xl px-4 pb-12">
        <div 
          className="prose prose-lg max-w-none text-foreground
                     prose-headings:text-foreground prose-headings:font-display
                     prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                     prose-strong:text-foreground prose-code:text-foreground
                     prose-img:rounded-lg prose-img:shadow-soft"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </div>

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="border-t bg-muted/30">
          <div className="container mx-auto max-w-4xl px-4 py-8">
            <div className="flex flex-wrap items-center gap-3">
              <Tag className="size-5 text-muted-foreground" />
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center rounded-md bg-accent px-3 py-1 text-sm text-accent-foreground"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {post.author && (
        <div className="border-t">
          <div className="container mx-auto max-w-4xl px-4 py-12">
            <div className="flex items-start gap-4 rounded-xl bg-muted/50 p-6">
              <Avatar className="size-16">
                {post.author.avatar && (
                  <AvatarImage 
                    src={post.author.avatar["96"]} 
                    alt={post.author.name}
                  />
                )}
                <AvatarFallback>
                  <User className="size-8" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  Written by {post.author.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Expert real estate analyst and digital marketing strategist at Nethill Limited
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPosts() {
  const { posts, loading } = usePosts();
  const currentPost = posts[0];

  if (loading || !currentPost) {
    return null;
  }

  return (
    <PostsProvider wp_query={{ 
        per_page: 3,
        exclude: [parseInt(currentPost.id)],
        order: "desc",
        orderby: "date"
      }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia"]}>
      <RelatedPostsContent />
    </PostsProvider>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="bg-dark-background">
        <div className="container mx-auto max-w-6xl px-4 py-16">
          <h2 className="text-3xl font-bold text-foreground-dark mb-8">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-64 rounded-xl bg-muted/10 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="bg-dark-background">
      <div className="container mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-3xl font-bold text-foreground-dark mb-8">Related Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <article
              key={post.id}
              className="group rounded-xl overflow-hidden bg-background/5 border border-foreground-dark/10
                         hover:bg-background/10 hover:border-foreground-dark/20
                         transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]
                         hover:-translate-y-1 hover:shadow-medium"
            >
              {post.featuredImage && (
                <div className="relative overflow-hidden h-48">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[var(--duration-slow)]"
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-foreground-dark mb-2 line-clamp-2">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <div 
                    className="text-sm text-foreground-dark/70 mb-4 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                {post.date && (
                  <time className="text-xs text-foreground-dark/50" dateTime={post.date}>
                    {new Date(post.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })}
                  </time>
                )}
                {post.link && (
                  <Link to={post.link} className="mt-4 inline-block">
                    <Button variant="outline" size="sm" className="text-foreground-dark border-foreground-dark/20 hover:bg-foreground-dark/10">
                      Read More →
                    </Button>
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <>
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      <RelatedPosts />
    </>
  );
}